import { FC, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import type { DTOBadge } from 'src/apis/dto/DTOShopModule';
import IcHistory from 'src/assets/images/icHistory.svg';
import useRemainingTimer from 'src/hooks/useRemainingTimer';
const IconWrapper = styled.div`
  width: 13px;
  height: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const TimerLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  background-color: ${({
  theme
}) => theme.color.accentH3};
  border-radius: 4px;
  max-width: 120px;
`;
const TimerIcon = styled(IcHistory)`
  width: 11px;
  height: 11px;
  color: ${({
  theme
}) => theme.color.contentDeep};
  ${({
  theme
}) => theme.isRtl && 'transform: scaleX(-1);'}
`;
const TimerText = styled.span`
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
  color: ${({
  theme
}) => theme.color.surfaceDeep};
`;
interface TimerLabelProps {
  endDate: string | number;
  onTimerOver?: () => void;
}
export const TimerLabel: FC<TimerLabelProps> = ({
  endDate,
  onTimerOver
}) => {
  const memoizedEndDate = useMemo(() => new Date(endDate), [endDate]);
  const {
    remainingTime,
    start,
    isTimerOver
  } = useRemainingTimer(memoizedEndDate);
  useEffect(() => {
    start();
  }, [start]);
  useEffect(() => {
    if (isTimerOver) {
      onTimerOver?.();
    }
  }, [isTimerOver, onTimerOver]);
  return <TimerLabelWrapper data-sentry-element="TimerLabelWrapper" data-sentry-component="TimerLabel" data-sentry-source-file="ShopLabels.tsx">
      <IconWrapper data-sentry-element="IconWrapper" data-sentry-source-file="ShopLabels.tsx">
        <TimerIcon data-sentry-element="TimerIcon" data-sentry-source-file="ShopLabels.tsx" />
      </IconWrapper>
      <TimerText data-sentry-element="TimerText" data-sentry-source-file="ShopLabels.tsx">{remainingTime.text}</TimerText>
    </TimerLabelWrapper>;
};
const DiscountLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  background-color: ${({
  theme
}) => theme.color.surface2};
  border-radius: 4px;
  max-width: 120px;
`;
const DiscountIcon = styled.img`
  width: 11px;
  height: 11px;
  ${({
  theme
}) => theme.isRtl && 'transform: scaleX(-1);'}
`;
const DiscountText = styled.span`
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
  color: ${({
  theme
}) => theme.color.accentH1};
`;
interface DiscountLabelProps {
  percent: number;
}
export const DiscountLabel: FC<DiscountLabelProps> = ({
  percent
}) => {
  const {
    t
  } = useTranslation();
  return <DiscountLabelWrapper data-sentry-element="DiscountLabelWrapper" data-sentry-component="DiscountLabel" data-sentry-source-file="ShopLabels.tsx">
      <IconWrapper data-sentry-element="IconWrapper" data-sentry-source-file="ShopLabels.tsx">
        <DiscountIcon src='/images/icons/imgGift.png' alt='discount' data-sentry-element="DiscountIcon" data-sentry-source-file="ShopLabels.tsx" />
      </IconWrapper>
      <DiscountText data-sentry-element="DiscountText" data-sentry-source-file="ShopLabels.tsx">{t('web_starterpackage_discount', {
        percent
      })}</DiscountText>
    </DiscountLabelWrapper>;
};
const ShopBadgeLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  background-color: ${({
  theme
}) => theme.color.surface2};
  border-radius: 4px;
  white-space: nowrap;
`;
const ShopBadgeIcon = styled.img`
  width: 11px;
  height: 11px;
  ${({
  theme
}) => theme.isRtl && 'transform: scaleX(-1);'}
`;
const ShopBadgeText = styled.span<{
  $color: string;
}>`
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
  color: ${({
  $color
}) => $color};
`;
export const ShopBadgeLabel: FC<DTOBadge> = ({
  text,
  iconUrl,
  mainColor
}) => {
  if (!text) return null;
  return <ShopBadgeLabelWrapper data-sentry-element="ShopBadgeLabelWrapper" data-sentry-component="ShopBadgeLabel" data-sentry-source-file="ShopLabels.tsx">
      {iconUrl && <ShopBadgeIcon src={iconUrl} alt='' />}
      <ShopBadgeText $color={mainColor} data-sentry-element="ShopBadgeText" data-sentry-source-file="ShopLabels.tsx">{text}</ShopBadgeText>
    </ShopBadgeLabelWrapper>;
};
export const DiscountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 4px;
  background-color: ${({
  theme
}) => theme.color.primaryDeep};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
  color: ${({
  theme
}) => theme.color.accentH1};
  white-space: nowrap;
`;